import React, { useEffect, useState } from 'react';
import {
  Table, Button, Input, Modal, Form,
  Typography, Space, Popconfirm, Alert, Empty, Drawer, Avatar, theme as antdTheme,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, UnorderedListOutlined, SearchOutlined,
  TeamOutlined, UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;

const Lists = () => {
  const { token: themeToken } = antdTheme.useToken();
  const [lists,        setLists]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [modalOpen,    setModalOpen]    = useState(false);
  const [creating,     setCreating]     = useState(false);
  const [search,       setSearch]       = useState('');

  // Subscriber drawer
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [drawerList,   setDrawerList]   = useState(null);   // { id, name }
  const [subSearch,    setSubSearch]    = useState('');
  const [subscribers,  setSubscribers]  = useState([]);
  const [subLoading,   setSubLoading]   = useState(false);

  const [form] = Form.useForm();
  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLists = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/lists', { headers });
      setLists(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load lists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLists(); }, []);

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      await axios.post('/api/lists', values, { headers });
      form.resetFields();
      setModalOpen(false);
      fetchLists();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create list');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/lists/${id}`, { headers });
      fetchLists();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete list');
    }
  };

  const openSubscribers = async (list) => {
    setDrawerList(list);
    setSubSearch('');
    setDrawerOpen(true);
    setSubLoading(true);
    try {
      const res = await axios.get(`/api/subscribers?list_id=${list.id}&limit=500`, { headers });
      setSubscribers(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
    } catch {
      setSubscribers([]);
    } finally {
      setSubLoading(false);
    }
  };

  const filtered    = lists.filter(l => l.name.toLowerCase().includes(search.toLowerCase()));
  const filteredSubs = subscribers.filter(s =>
    s.name?.toLowerCase().includes(subSearch.toLowerCase()) ||
    s.email?.toLowerCase().includes(subSearch.toLowerCase())
  );

  // ── Stat card computations ─────────────────────────────────────────────
  const totalLists       = lists.length;
  const activeLists      = lists.filter(l => (l.subscriber_count ?? 0) > 0).length;
  const totalSubscribers = lists.reduce((sum, l) => sum + (l.subscriber_count ?? 0), 0);
  const avgPerList       = totalLists > 0 ? Math.round(totalSubscribers / totalLists) : 0;

  const stats = [
    { label: 'Total Lists',        value: totalLists },
    { label: 'Active Lists',       value: activeLists },
    { label: 'Total Subscribers',  value: totalSubscribers.toLocaleString() },
    { label: 'Avg per List',       value: avgPerList.toLocaleString() },
  ];

  const columns = [
    {
      title: 'List Name', dataIndex: 'name', width: 180, ellipsis: true,
      render: name => (
        <Space size={10}>
          <div style={{
            width: 34, height: 34, background: 'var(--brand-tint)',
            borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <UnorderedListOutlined style={{ color: 'var(--brand-primary)', fontSize: 15 }} />
          </div>
          <Text strong style={{ fontSize: 14, wordBreak: 'break-word', minWidth: 80 }}>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Subscribers', dataIndex: 'subscriber_count', width: 160,
      render: (n, record) => (
        <button
          onClick={() => openSubscribers(record)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 999,
            background: 'var(--info-bg)',
            border: '1px solid color-mix(in srgb, var(--info) 30%, transparent)',
            color: 'var(--info)', fontWeight: 600, fontSize: 12,
            cursor: 'pointer', userSelect: 'none', lineHeight: '18px',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <TeamOutlined style={{ fontSize: 11 }} />
          <span>{(n ?? 0).toLocaleString()}</span>
          <span style={{ fontWeight: 400, opacity: 0.75, fontFamily: 'inherit' }}>
            {(n ?? 0) !== 1 ? 'subscribers' : 'subscriber'}
          </span>
        </button>
      ),
    },
    {
      title: 'Created', dataIndex: 'created_at', width: 120, responsive: ['md'],
      render: d => (
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
          {new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
        </span>
      ),
    },
    {
      title: '', align: 'right', width: 90, fixed: 'right',
      render: (_, record) => (
        <Popconfirm
          title="Delete this list?"
          description="All subscriber mappings for this list will be removed."
          onConfirm={() => handleDelete(record.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <Button danger icon={<DeleteOutlined />} size="small">Delete</Button>
        </Popconfirm>
      ),
    },
  ];

  const subColumns = [
    {
      title: 'Subscriber', key: 'name', ellipsis: true,
      render: (_, r) => {
        const displayName = [r.first_name, r.last_name].filter(Boolean).join(' ') || r.name || '';
        return (
          <Space size={10}>
            <Avatar
              size={34}
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), #2e6bf5)', fontSize: 13, fontWeight: 700, flexShrink: 0 }}
            >
              {displayName ? displayName.charAt(0).toUpperCase() : <UserOutlined />}
            </Avatar>
            <div>
              <Text strong style={{ fontSize: 13, display: 'block' }}>{displayName || '—'}</Text>
              <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>{r.email}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Location', key: 'location', width: 140,
      render: (_, r) => {
        const loc = [r.city, r.state, r.country].filter(Boolean).join(', ');
        return <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>{loc || '—'}</Text>;
      },
    },
    {
      title: 'Added', dataIndex: 'created_at', width: 110,
      render: d => (
        <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>
          {new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </Text>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — no inline title (topbar owns it) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => setModalOpen(true)}
          style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}
        >
          New List
        </Button>
      </div>

      {/* 4-stat header row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
        {stats.map(s => (
          <div key={s.label} style={{
            borderRadius: 'var(--r-lg)', border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-sm)', background: 'var(--surface)',
            padding: '14px 18px',
          }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text)', lineHeight: 1.1 }}>
              {loading ? '—' : s.value}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError('')}
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }}
        />
      )}

      <div style={{
        borderRadius: 'var(--r-lg)', border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)', background: 'var(--surface)', overflow: 'hidden',
      }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <Input
            placeholder="Search lists…"
            prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', maxWidth: 280, borderRadius: 'var(--r-md)' }}
            allowClear
            size="small"
          />
        </div>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No lists yet. Create your first list!" /> }}
          pagination={{ pageSize: 10, showSizeChanger: false, showTotal: t => `${t} list${t !== 1 ? 's' : ''}`, style: { padding: '8px 16px' } }}
          scroll={{ x: 'max-content' }}
          style={{ borderRadius: 0 }}
        />
      </div>

      {/* ── Create List Modal ── */}
      <Modal
        title={
          <Space>
            <div style={{
              width: 32, height: 32, background: 'var(--brand-tint)',
              borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <UnorderedListOutlined style={{ color: 'var(--brand-primary)' }} />
            </div>
            <span>Create Mailing List</span>
          </Space>
        }
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnHidden
        width={440}
      >
        <Form form={form} onFinish={handleCreate} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="name" label="List Name" rules={[{ required: true, message: 'Please enter a list name' }]}>
            <Input placeholder="e.g., Monthly Newsletter" size="large" style={{ borderRadius: 'var(--r-md)' }} autoFocus />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={creating} style={{ borderRadius: 8 }}>
                Create List
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Subscribers Drawer ── */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={560}
        destroyOnHidden
        title={
          <Space size={10}>
            <div style={{
              width: 32, height: 32, background: 'var(--brand-tint)',
              borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TeamOutlined style={{ color: 'var(--brand-primary)' }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>{drawerList?.name}</div>
              <div style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-3)' }}>
                {subLoading ? 'Loading…' : `${subscribers.length} subscriber${subscribers.length !== 1 ? 's' : ''}`}
              </div>
            </div>
          </Space>
        }
      >
        <Input
          placeholder="Search by name or email…"
          prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
          value={subSearch}
          onChange={e => setSubSearch(e.target.value)}
          allowClear
          style={{ borderRadius: 'var(--r-md)', marginBottom: 16 }}
        />
        <Table
          dataSource={filteredSubs}
          columns={subColumns}
          rowKey="id"
          loading={subLoading}
          size="small"
          pagination={{ pageSize: 10, showTotal: t => `${t} subscriber${t !== 1 ? 's' : ''}`, showSizeChanger: false }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No subscribers in this list" /> }}
          scroll={{ x: 'max-content' }}
        />
      </Drawer>
    </div>
  );
};

export default Lists;
